/**
 * THE REQUESTER'S CONTACT US CHOICES ON THE RECORD-FILLED BLOCK (2026-09-25, Jack).
 *
 * `ECH_ContactUsDefault` declares a `dataSource`, so every division gets the SAME block
 * and its photo, links and button labels come from the division's row in Layne's Contact
 * Us data extension (`echContactPatch`). Four things a requester could not do on it:
 *
 *   1. leave the team photos out — the 09-23 "No photos" switch only ever reached the
 *      older per-division swap block (`echIscPatch`), which stands down for this one;
 *   2. pick the button wording — `Cta1Label` is "Set from your division's record";
 *   3. add a second button — only a division whose row has `Cta2_Url` (NEV) gets one;
 *   4. swap the order of the two buttons.
 *
 * All four are stored on the block as `__` keys (never block fields, so they never meet
 * a column, a token or the payload by name) and applied here, as a wrapper around the
 * lookup the treatment pass already uses. Layne's markup is untouched: "No photos" picks
 * her own `none` treatment (the house icon, which is what a division without photos
 * already ships), and the buttons are her two button cells, the second one behind her
 * own `IFCOLUMN:Cta2_Url` region. The row stays the default for everything a requester
 * has not chosen, and clearing a choice puts the row's value back.
 */
import { normTok, type TokLookup } from "./echContactTreatment";

/** the same flag the older block's switch writes, so a block keeps its choice either way */
export const CU_NO_PHOTOS = "__iscNoPhotos";
/** the first button's wording, when the requester picked one */
export const CU_LABEL1 = "__cuLabel1";
/** the second button: "add" = the requester added one, "off" = removed the division's */
export const CU_BTN2 = "__cuBtn2";
export const CU_LABEL2 = "__cuLabel2";
export const CU_URL2 = "__cuUrl2";
/** "1" = the two buttons ship in the opposite order */
export const CU_SWAP = "__cuSwap";

export interface CuButton { label: string; url: string }
export interface CuState {
  /** the division's row has a photo treatment with an image in it */
  hasPhotos: boolean;
  noPhotos: boolean;
  /** the button the division's first link belongs to */
  b1: CuButton;
  /** the second button as it will ship, or null when there is none */
  b2: CuButton | null;
  /** the division's own row carries a second button */
  b2FromRow: boolean;
  swapped: boolean;
}

const S = (v: unknown): string => (v === undefined || v === null ? "" : String(v)).trim();

/** does the row answer a photo? (the three treatments, read the way the pass reads them) */
function rowHasPhotos(get: TokLookup): boolean {
  const t = get("image_treatment").toLowerCase();
  if (t === "group") return !!get("GroupImageUrl");
  if (t === "headshots") for (let n = 1; n <= 5; n++) if (get("Headshot" + n + "Url")) return true;
  return false;
}

/**
 * Where the block stands, from its data and the division lookup (`contactLookup`).
 * `get` is the UNWRAPPED lookup — the division's answers plus the declared fields.
 */
export function cuState(data: Record<string, unknown> | null | undefined, get: TokLookup): CuState {
  const d = data || {};
  const rowLabel1 = get("Cta1Label") || (get.def ? get.def("Cta1Label") : "") || "Contact Us";
  const b1: CuButton = { label: S(d[CU_LABEL1]) || rowLabel1, url: get("ContactUrl") };
  const rowUrl2 = get("Cta2_Url");
  const rowLabel2 = get("Cta2Label");
  const b2FromRow = !!rowUrl2;
  const mode = S(d[CU_BTN2]);
  const on = mode === "off" ? false : (mode === "add" || b2FromRow);
  const b2: CuButton | null = on ? { label: S(d[CU_LABEL2]) || rowLabel2, url: S(d[CU_URL2]) || rowUrl2 } : null;
  return {
    hasPhotos: rowHasPhotos(get),
    noPhotos: S(d[CU_NO_PHOTOS]) === "1",
    b1,
    b2,
    b2FromRow,
    swapped: !!b2 && S(d[CU_SWAP]) === "1",
  };
}

/** the two buttons in the order they ship */
export function cuOrder(st: CuState): [CuButton, CuButton | null] {
  return st.swapped && st.b2 ? [st.b2, st.b1] : [st.b1, st.b2];
}

/**
 * The lookup the treatment pass should use: the division's answers with the requester's
 * choices over them. A second button ships only when it has BOTH a label and a link —
 * its region is deleted otherwise, so a half-set button can never print `{{Cta2Label}}`
 * or point nowhere (the Done check names what is missing).
 */
export function cuLookup(data: Record<string, unknown> | null | undefined, get: TokLookup): TokLookup {
  const st = cuState(data, get);
  const [first, second] = cuOrder(st);
  const ship2 = !!second && !!second.url && !!second.label;
  const over: Record<string, string> = {
    contacturl: first.url,
    cta1url: first.url,
    cta1label: first.label,
    cta2url: ship2 ? second!.url : "",
    cta2label: ship2 ? second!.label : "",
  };
  if (st.noPhotos) over.imagetreatment = "none";
  const wrapped: TokLookup = (name) => {
    const n = normTok(name);
    return Object.prototype.hasOwnProperty.call(over, n) ? over[n] : get(name);
  };
  /* a default only ever fills an EMPTY answer; the overridden names are always answered
     (or deliberately empty, for a second button that is not shipping), so their defaults
     must not bring it back */
  wrapped.def = (name) => (Object.prototype.hasOwnProperty.call(over, normTok(name)) ? "" : (get.def ? get.def(name) : ""));
  return wrapped;
}

/** true when the block holds any requester choice this file applies */
export function cuHasChoices(data: Record<string, unknown> | null | undefined): boolean {
  const d = data || {};
  return [CU_NO_PHOTOS, CU_LABEL1, CU_BTN2, CU_LABEL2, CU_URL2, CU_SWAP].some((k) => S(d[k]) !== "");
}
